import { PocInput, PocTypography } from 'commons/components/components-wrapper';
import { InputError, InputProtection, InputType, InputValidity, TypographyVariant } from '@one/web-components';
import { useTranslation } from 'core/i18n/i18n';
import { withOptionalLabel } from 'commons/utils/optional-label.utils';
import { CustomValidationCode } from 'core/utils/backend-service';
import { KeyboardEvent, useState } from 'react';
import { AssistiveText, ExtendedValidationMessages } from '../assistive-text';
import { getInlineOrBlockClassName } from '../utils/utils';
import { BROWSER_AUTOFILL_DISABLED } from './input.constants';
import { AssistiveTextLayout, InputLayout } from './input.style';

export interface InputProps {
    id?: string;
    name: string;
    label?: string;
    ariaLabel?: string;
    placeholder: string;
    noClearIcon?: boolean;
    type?: InputType;
    value?: string;
    reference?: React.Ref<HTMLOwcInputElement>;
    autofocus?: boolean;
    required?: boolean;
    optional?: boolean;
    pattern?: string;
    minValue?: number;
    maxValue?: number;
    minlength?: number;
    maxlength?: number;
    validity?: InputValidity;
    showCharCount?: boolean;
    spaceForError?: boolean;
    floatingAssistiveText?: boolean;
    variant?: TypographyVariant;
    inline?: boolean;
    disabled?: boolean;
    className?: string;
    suffix?: React.ReactElement;
    assistiveText?: string;
    validationMessages?: ExtendedValidationMessages;
    customValidationCode?: CustomValidationCode | InputError;
    protection?: InputProtection;
    autoComplete?: AutoFill | typeof BROWSER_AUTOFILL_DISABLED;
    onValidity?: (field: string, validity: InputValidity) => void;
    onChange: (field: string, value: string | null) => void;
    onKeyDown?: (ev: KeyboardEvent<HTMLOwcInputElement> | undefined) => void;
    onCustomBlur?: VoidFunction;
}

export const Input: React.FC<InputProps> = (props) => {
    const {
        id,
        name,
        label,
        ariaLabel,
        minValue,
        maxValue,
        placeholder,
        noClearIcon = true,
        type = 'text',
        value = undefined,
        reference = undefined,
        autofocus = false,
        required = false,
        optional = !required,
        minlength = undefined,
        maxlength = undefined,
        pattern = undefined,
        validity,
        showCharCount = undefined,
        variant = 'subtitle1',
        spaceForError = false,
        floatingAssistiveText = false,
        inline = false,
        disabled = false,
        className,
        suffix,
        assistiveText,
        validationMessages = {},
        customValidationCode,
        protection = 'none',
        autoComplete = 'off',
        onValidity,
        onChange,
        onKeyDown,
        onCustomBlur,
    } = props;

    const [focused, setFocused] = useState(false);
    const { t } = useTranslation();

    return (
        <InputLayout className={`${getInlineOrBlockClassName(inline)} ${className}`}>
            {label ? (
                <PocTypography
                    element="span"
                    variant={variant}
                    className={`${variant === 'subtitle1' ? 'label' : ''} ${getInlineOrBlockClassName(inline)}`}
                    data-testid={`input-label-${name}`}
                >
                    {withOptionalLabel(t, t(label), optional)}
                </PocTypography>
            ) : undefined}

            <PocInput
                className={`${spaceForError ? 'space-for-error' : ''} ${floatingAssistiveText ? 'floating-assistive-text' : ''} ${getInlineOrBlockClassName(inline)}`}
                id={id ?? name}
                name={name}
                label={name}
                aria-label={ariaLabel ?? name}
                data-testid={`input-form-${name}`}
                placeholder={t(placeholder) || ''}
                variant="outlined"
                size="small"
                no-clear-icon={noClearIcon}
                validity={validity}
                type={type}
                value={value}
                required={required}
                pattern={pattern ?? String.raw`^$|(\s*\S+)+`}
                minlength={minlength}
                maxlength={maxlength}
                autoFocus={autofocus}
                min={minValue}
                max={maxValue}
                ref={reference}
                protection={protection}
                nativeInputProps={{ autocomplete: autoComplete as AutoFill }}
                showCharCount={showCharCount ?? focused}
                controlled
                controlledValidity={validity !== undefined || onValidity !== undefined}
                onValueChange={(ev) => onChange(name, ev.detail)}
                onValidityChange={(ev) => onValidity?.(name, ev.detail)}
                onFocus={showCharCount == null ? () => setFocused(true) : undefined}
                onBlur={
                    showCharCount == null
                        ? () => {
                              onCustomBlur?.();
                              setFocused(false);
                          }
                        : undefined
                }
                onKeyDown={(ev) => onKeyDown?.(ev)}
                disabled={disabled}
            >
                <div slot="suffix">{suffix}</div>
                <AssistiveTextLayout slot="assistive-text" data-testid={`input-assistive-text-${name}`}>
                    <AssistiveText
                        validity={validity}
                        validationMessages={validationMessages}
                        customValidationCode={customValidationCode}
                        helperText={assistiveText ? t(assistiveText) : undefined}
                    />
                </AssistiveTextLayout>
            </PocInput>
        </InputLayout>
    );
};
